/**
 * REK-1985 (SSO.4): module-level registry bridging the Clerk session into the
 * Supabase client singleton.
 *
 * The Supabase client is created at module-import time with an `accessToken`
 * closure (see src/integrations/supabase/client.ts), but the Clerk instance
 * only exists once React has mounted `ClerkAuthProvider`. This registry is the
 * seam: the provider registers a token getter / sign-out handler / resolved
 * legacy user here, and non-React modules (the Supabase client, auth-bridge
 * helpers) read through it.
 *
 * Deliberately framework-free (no React imports) so it can be imported from
 * the Supabase client module without cycles, and inert under
 * VITE_AUTH_PROVIDER=supabase (nothing ever registers).
 */
import { safeError } from '@/lib/safeLog';

export interface ClerkTokenOptions {
  /**
   * Bypass clerk-js's in-memory token cache and mint a fresh token. The
   * Clerk analogue of `supabase.auth.refreshSession()`.
   */
  forceRefresh?: boolean;
}

export type ClerkTokenGetter = (
  options?: ClerkTokenOptions,
) => Promise<string | null>;

export interface ClerkAuthUser {
  /** Legacy `profiles.id` UUID (Clerk `external_id`) — NOT the Clerk user id. */
  id: string;
  email: string | null;
}

/**
 * REK-1986 (SSO.5): terminal outcome of ClerkAuthProvider's post-sign-in
 * profile resolution. Custom-flow callers (login OTP verify, signup verify)
 * wait on this so they can distinguish "session granted" from "denied:
 * account locked" / "denied: verification failed" instead of navigating
 * before the provider has resolved the legacy identity.
 */
export type ClerkSessionResolution = 'granted' | 'denied-locked' | 'denied-error';

/**
 * Sign-in method used by the most recent custom-flow completion (REK-1986;
 * REK-2186: social SSO records the Clerk strategy as `oauth_<provider>` —
 * never a bare provider slug like `google`.
 */
export type ClerkSignInMethod = 'email' | 'phone' | `oauth_${string}`;

let tokenGetter: ClerkTokenGetter | null = null;
let cachedToken: string | null = null;
let authUser: ClerkAuthUser | null = null;
let signOutHandler: (() => Promise<void>) | null = null;
let sessionResolution: ClerkSessionResolution | null = null;
let resolutionWaiters: Array<(resolution: ClerkSessionResolution) => void> = [];
let lastSignInMethod: ClerkSignInMethod | null = null;

/** Registered by ClerkAuthProvider as soon as clerk-js is available. */
export function registerClerkTokenGetter(getter: ClerkTokenGetter | null): void {
  tokenGetter = getter;
  if (!getter) cachedToken = null;
}

/**
 * Registered by ClerkAuthProvider once the legacy UUID is resolved (and
 * cleared on sign-out). Lets non-React code (auth-bridge) answer "who is
 * signed in" synchronously without a network round trip.
 */
export function registerClerkAuthUser(user: ClerkAuthUser | null): void {
  authUser = user;
}

export function getClerkAuthUser(): ClerkAuthUser | null {
  return authUser;
}

/** Registered by ClerkAuthProvider; invoked via the auth-bridge `authSignOut`. */
export function registerClerkSignOut(handler: (() => Promise<void>) | null): void {
  signOutHandler = handler;
}

export async function clerkSignOut(): Promise<void> {
  if (!signOutHandler) return;
  await signOutHandler();
}

/**
 * The `accessToken` closure for the Supabase client. Returns the current
 * Clerk session token (clerk-js caches tokens internally and refreshes when
 * near expiry, so this is cheap to call per request) or null when signed out
 * / Clerk not yet loaded — supabase-js then falls back to the anon key.
 */
export async function getClerkSupabaseToken(
  options?: ClerkTokenOptions,
): Promise<string | null> {
  if (!tokenGetter) return null;
  try {
    const token = await tokenGetter(options);
    cachedToken = token;
    return token;
  } catch (e) {
    safeError('Failed to get Clerk session token:', e);
    return null;
  }
}

/**
 * Last token returned by `getClerkSupabaseToken`. The ClerkAuthProvider keeps
 * this fresh on a 50s heartbeat (Clerk tokens live 60s) so the context
 * `session.access_token` getter can be synchronous. Prefer the async getter
 * whenever awaiting is possible.
 */
export function getCachedClerkToken(): string | null {
  return cachedToken;
}

/**
 * Called by ClerkAuthProvider when a session's legacy-identity resolution
 * reaches a terminal state: `granted` (profile resolved, not locked),
 * `denied-locked` (profiles.is_locked / Clerk ban), or `denied-error`
 * (fail-closed lookup error or provisioning timeout). Resolves every
 * in-flight `waitForClerkSessionResolution` promise.
 */
export function notifyClerkSessionResolution(resolution: ClerkSessionResolution): void {
  sessionResolution = resolution;
  const waiters = resolutionWaiters;
  resolutionWaiters = [];
  waiters.forEach((resolve) => resolve(resolution));
}

/**
 * Called by ClerkAuthProvider when a new resolution attempt starts (fresh
 * session) and when the session ends, so a stale terminal state from a
 * previous session can never satisfy a new waiter.
 */
export function resetClerkSessionResolution(): void {
  sessionResolution = null;
}

/**
 * Wait for the ClerkAuthProvider to finish resolving the active session.
 * Resolves immediately when a terminal state is already recorded (fast
 * grant that beat the waiter). `timeout` means the provider never reported —
 * callers should surface a neutral retry error.
 */
export function waitForClerkSessionResolution(
  timeoutMs: number,
): Promise<ClerkSessionResolution | 'timeout'> {
  if (sessionResolution) return Promise.resolve(sessionResolution);
  return new Promise((resolve) => {
    const waiter = (resolution: ClerkSessionResolution) => {
      clearTimeout(timer);
      resolve(resolution);
    };
    const timer = setTimeout(() => {
      resolutionWaiters = resolutionWaiters.filter((w) => w !== waiter);
      resolve('timeout');
    }, timeoutMs);
    resolutionWaiters.push(waiter);
  });
}

/**
 * Recorded by the custom-flow transport (src/lib/clerk-auth-flows.ts) when a
 * sign-in/sign-up completes, consumed once by ClerkAuthProvider's `login`
 * analytics event so phone sign-ins report `method: 'phone'`.
 */
export function setLastSignInMethod(method: ClerkSignInMethod): void {
  lastSignInMethod = method;
}

export function consumeLastSignInMethod(): ClerkSignInMethod | null {
  const method = lastSignInMethod;
  lastSignInMethod = null;
  return method;
}

/** Test-only: reset all registry state between test cases. */
export function __resetClerkTokenRegistryForTests(): void {
  tokenGetter = null;
  cachedToken = null;
  authUser = null;
  signOutHandler = null;
  sessionResolution = null;
  resolutionWaiters = [];
  lastSignInMethod = null;
}
